/**
 * AccessibilityMenu.js
 * Header toolbar button that opens a popover with four accessibility toggles:
 *   - Text Size: small / normal / large
 *   - High Contrast: boosts contrast filter
 *   - Reduce Motion: disables CSS transitions and animations
 *   - Dyslexia Font: switches to a more readable font
 *
 * Settings are persisted to localStorage under 'ekamanam_a11y' and applied
 * as data attributes / CSS classes on <html>.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Tooltip,
  IconButton,
  Popover,
  Typography,
  Stack,
  Divider,
  Switch,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  TextFields as TextFieldsIcon,
  Contrast as ContrastIcon,
  MotionPhotosOff as MotionOffIcon,
  FontDownload as FontIcon,
} from '@mui/icons-material';

const STORAGE_KEY = 'ekamanam_a11y';

function loadSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return { fontSize: 'normal', highContrast: false, reduceMotion: false, dyslexiaFont: false };
}

function applySettings({ fontSize, highContrast, reduceMotion, dyslexiaFont }) {
  const root = document.documentElement;

  // Font size — inline style wins over all CSS (including MUI emotion)
  if (fontSize === 'large') {
    root.style.setProperty('font-size', '120%');
  } else if (fontSize === 'small') {
    root.style.setProperty('font-size', '87.5%');
  } else {
    root.style.removeProperty('font-size');
  }

  // High contrast — apply filter to <body> (more reliable than <html> in all browsers)
  if (document.body) {
    document.body.style.filter = highContrast ? 'contrast(1.5) brightness(0.95)' : '';
  }

  // Reduce motion + dyslexia font via CSS classes (use !important in CSS)
  root.classList.toggle('reduce-motion', reduceMotion);
  root.classList.toggle('dyslexia-font', dyslexiaFont);

  // Keep attribute for CSS fallback
  root.setAttribute('data-font-size', fontSize);
}

export default function AccessibilityMenu() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [fontSize, setFontSize]           = useState('normal');
  const [highContrast, setHighContrast]   = useState(false);
  const [reduceMotion, setReduceMotion]   = useState(false);
  const [dyslexiaFont, setDyslexiaFont]   = useState(false);

  // Load from localStorage on mount
  useEffect(() => {
    const s = loadSettings();
    setFontSize(s.fontSize);
    setHighContrast(s.highContrast);
    setReduceMotion(s.reduceMotion);
    setDyslexiaFont(s.dyslexiaFont);
    applySettings(s);
  }, []);

  // Apply and persist on every change
  useEffect(() => {
    const s = { fontSize, highContrast, reduceMotion, dyslexiaFont };
    applySettings(s);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* ignore */ }
  }, [fontSize, highContrast, reduceMotion, dyslexiaFont]);

  const open = Boolean(anchorEl);
  const isActive = highContrast || reduceMotion || dyslexiaFont || fontSize !== 'normal';

  return (
    <>
      <Tooltip title="Accessibility options">
        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label="Open accessibility options"
          aria-expanded={open}
          aria-haspopup="true"
          size="small"
          sx={{
            color: isActive ? 'primary.main' : 'text.secondary',
            border: isActive ? '1.5px solid' : '1.5px solid transparent',
            borderColor: isActive ? 'primary.main' : 'transparent',
            borderRadius: 1.5,
          }}
        >
          <TextFieldsIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: { width: 280, p: 2, borderRadius: 2.5 },
          role: 'dialog',
          'aria-label': 'Accessibility settings',
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
          Accessibility
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {/* Text Size */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextFieldsIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight={600}>Text Size</Typography>
          </Stack>
          <ToggleButtonGroup
            value={fontSize}
            exclusive
            onChange={(_, v) => { if (v) setFontSize(v); }}
            size="small"
            aria-label="Text size"
          >
            <ToggleButton value="small" aria-label="Small text"
              sx={{ px: 1.25, py: 0.5, fontSize: '0.7rem', fontWeight: 700 }}>A−</ToggleButton>
            <ToggleButton value="normal" aria-label="Normal text"
              sx={{ px: 1.25, py: 0.5, fontSize: '0.75rem', fontWeight: 700 }}>A</ToggleButton>
            <ToggleButton value="large" aria-label="Large text"
              sx={{ px: 1.25, py: 0.5, fontSize: '0.85rem', fontWeight: 700 }}>A+</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {/* High Contrast */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <ContrastIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Box>
              <Typography variant="body2" fontWeight={600}>High Contrast</Typography>
              <Typography variant="caption" color="text.secondary">Boost colour contrast</Typography>
            </Box>
          </Stack>
          <Switch
            checked={highContrast}
            onChange={(e) => setHighContrast(e.target.checked)}
            size="small"
            inputProps={{ 'aria-label': 'Toggle high contrast' }}
          />
        </Stack>

        {/* Reduce Motion */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <MotionOffIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Box>
              <Typography variant="body2" fontWeight={600}>Reduce Motion</Typography>
              <Typography variant="caption" color="text.secondary">Disable animations</Typography>
            </Box>
          </Stack>
          <Switch
            checked={reduceMotion}
            onChange={(e) => setReduceMotion(e.target.checked)}
            size="small"
            inputProps={{ 'aria-label': 'Toggle reduce motion' }}
          />
        </Stack>

        {/* Dyslexia Font */}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <FontIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Box>
              <Typography variant="body2" fontWeight={600}>Dyslexia Font</Typography>
              <Typography variant="caption" color="text.secondary">More readable typeface</Typography>
            </Box>
          </Stack>
          <Switch
            checked={dyslexiaFont}
            onChange={(e) => setDyslexiaFont(e.target.checked)}
            size="small"
            inputProps={{ 'aria-label': 'Toggle dyslexia-friendly font' }}
          />
        </Stack>
      </Popover>
    </>
  );
}
